/**
 * 智能体团队状态管理 Store
 *
 * 使用 zustand 管理智能体团队会话状态，包括会话信息、对话历史、活跃智能体和实时连接。
 * 已迁移至 SSE + REST API，移除 WebSocket 依赖。
 */

import { create } from 'zustand';
import { getAgentTeamApi } from '../services/agentTeamApi';
import { sanitizeErrorMessage } from '../utils/validation';
import { useAuthStore } from './authStore';
import {
  AgentTeamSession,
  AgentConversationMessage,
  ActiveAgent,
  SessionStatus,
  AgentStatusUpdate,
  SSEEvent,
  SessionCreateRequest,
  SendMessageRequest,
  STAGE_LABELS,
} from '../types/agentTeam';
import type { StreamingMessagePart } from '../types/chat';
import type { WorkflowStageState, WorkflowStageName } from '../types/agentTeam';
import type { QuestionAskedEvent } from '../types/chat';

interface StreamingState {
  isStreaming: boolean;
  streamingMessageId: number | null;
  streamingContent: string;
  streamingRole: string;
  streamingSenderName: string;
  parts: StreamingMessagePart[];
  sequence: number;
}

export type WorkerStreamStatus = 'running' | 'completed' | 'failed';

export interface WorkerStreamState {
  taskId: number;
  agentType: string;
  description: string;
  status: WorkerStreamStatus;
  streamingContent: string;
  parts: StreamingMessagePart[];
  sequence: number;
}

interface AgentTeamState {
  session: AgentTeamSession | null;
  workspaceId: number | null;
  conversations: AgentConversationMessage[];
  activeAgents: Map<string, ActiveAgent>;
  loading: boolean;
  error: string | null;
  processing: boolean;
  streaming: StreamingState;
  workerStreams: Map<number, WorkerStreamState>;
  stages: WorkflowStageState[];
  pendingQuestion: QuestionAskedEvent | null;

  sessions: AgentTeamSession[];
  sessionsLoaded: boolean;

  sseConnected: boolean;
  sseAbortController: AbortController | null;

  getSession: () => AgentTeamSession | null;
  getConversations: () => AgentConversationMessage[];
  getActiveAgents: () => ActiveAgent[];
  getAgentByName: (name: string) => ActiveAgent | undefined;
  isProcessing: () => boolean;
  isStreaming: () => boolean;
  getStreamingContent: () => string;
  getStreamingParts: () => StreamingMessagePart[];
  getStages: () => WorkflowStageState[];
  getCurrentStage: () => WorkflowStageState | null;
  getPendingQuestion: () => QuestionAskedEvent | null;

  getWorkerStreams: () => Map<number, WorkerStreamState>;
  getWorkerStream: (taskId: number) => WorkerStreamState | undefined;
  clearWorkerStream: (taskId: number) => void;
  clearAllWorkerStreams: () => void;

  initSession: (workspaceId: number, userCommand?: string) => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  addMessage: (message: AgentConversationMessage) => void;
  updateStreamingMessage: (content: string) => void;
  finishStreamingMessage: (messageId: number, fullContent: string) => void;
  updateAgentStatus: (update: AgentStatusUpdate) => void;
  addActiveAgent: (agent: ActiveAgent) => void;
  removeActiveAgent: (agentName: string, reason?: string) => void;
  updateAgentProgress: (agentName: string, progress: number, message?: string) => void;
  updateSessionStatus: (status: SessionStatus) => void;
  clearSession: () => void;
  setProcessing: (processing: boolean) => void;
  setError: (error: string | null) => void;
  handleQuestionAsked: (event: QuestionAskedEvent) => void;
  submitQuestionAnswer: (questionId: string, answers: string[][]) => Promise<void>;
  dismissQuestion: (questionId: string) => Promise<void>;

  startSSEStream: (content: string) => Promise<void>;
  cancelSSEStream: () => void;

  fetchSessions: (workspaceId: number, page?: number, pageSize?: number) => Promise<void>;
  deleteSession: (workspaceId: number, sessionId: string) => Promise<void>;

  handleSSEEvent: (event: SSEEvent) => void;

  setSseConnected: (connected: boolean) => void;

  cleanup: () => void;

  saveActiveSession: () => void;
  getSavedActiveSessionId: (workspaceId: number) => string | null;
  clearSavedSession: () => void;
  initializeWorkspace: (workspaceId: number) => Promise<void>;
  checkExecutionStatus: (sessionId: string) => Promise<{ is_executing: boolean; execution_state: Record<string, unknown> }>;
  subscribeProgress: (sessionId: string) => Promise<void>;
  cancelExecution: (sessionId: string) => Promise<boolean>;
  sendMessageToQueue: (sessionId: string, content: string) => Promise<any>;
}

export const useAgentTeamStore = create<AgentTeamState>()((set, get) => ({
  session: null,
  workspaceId: null,
  conversations: [],
  activeAgents: new Map(),
  loading: false,
  error: null,
  processing: false,
  streaming: {
    isStreaming: false,
    streamingMessageId: null,
    streamingContent: '',
    streamingRole: '',
    streamingSenderName: '',
    parts: [],
    sequence: 0,
  },
  workerStreams: new Map(),
  stages: [],
  pendingQuestion: null,
  sessions: [],
  sessionsLoaded: false,
  sseConnected: false,
  sseAbortController: null,

  getSession: () => get().session,
  getConversations: () => get().conversations,
  getActiveAgents: () => Array.from(get().activeAgents.values()),
  getAgentByName: (name: string) => get().activeAgents.get(name),
  isProcessing: () => get().processing,
  isStreaming: () => get().streaming.isStreaming,
  getStreamingContent: () => get().streaming.streamingContent,
  getStreamingParts: () => get().streaming.parts,
  getStages: () => get().stages,
  getCurrentStage: () => {
    const stages = get().stages;
    return stages.find(s => s.status === 'running') || stages.find(s => s.status === 'pending') || null;
  },
  getPendingQuestion: () => get().pendingQuestion,

  getWorkerStreams: () => get().workerStreams,
  getWorkerStream: (taskId: number) => get().workerStreams.get(taskId),
  clearWorkerStream: (taskId: number) => {
    set(state => {
      const next = new Map(state.workerStreams);
      next.delete(taskId);
      return { workerStreams: next };
    });
  },
  clearAllWorkerStreams: () => {
    set({ workerStreams: new Map() });
  },

  initSession: async (workspaceId: number, userCommand?: string) => {
    const state = get();
    if (state.loading || state.processing) return;

    set({ loading: true, error: null, workspaceId, processing: true });

    const abortController = new AbortController();

    const userMessage: AgentConversationMessage = {
      id: Date.now(),
      session_id: '',
      sequence_number: 1,
      role: 'user',
      speaker_name: '用户',
      agent_id: null,
      message_type: 'text',
      content: userCommand || '开始新会话',
      data: null,
      is_final: true,
      created_at: new Date().toISOString(),
    };

    try {
      const api = getAgentTeamApi();
      const request: SessionCreateRequest = {
        workspace_id: workspaceId,
        user_command: userCommand || '开始新会话',
      };

      set({
        conversations: [userMessage],
        sseAbortController: abortController,
        sseConnected: true,
        streaming: {
          isStreaming: true,
          streamingMessageId: null,
          streamingContent: '',
          streamingRole: 'orchestrator',
          streamingSenderName: '规划者',
          parts: [],
          sequence: 0,
        },
      });

      let sessionId: string | null = null;

      for await (const event of api.createSessionStream(request, abortController.signal)) {
        const data = event.data as any;

        if (data.session_id && !sessionId) {
          sessionId = data.session_id;

          userMessage.session_id = sessionId!;

          set({
            session: {
              id: sessionId!,
              workspace_id: workspaceId,
              user_id: 0,
              title: userCommand?.substring(0, 50) || '新会话',
              status: 'planning',
              metadata: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              completed_at: null,
            },
            loading: false,
            stages: [],
          });
        }

        get().handleSSEEvent(event);

        await new Promise<void>(r => setTimeout(r, 0));
      }

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('[AgentTeamStore] SSE流已取消');
      } else {
        const errorMessage = sanitizeErrorMessage(error, '创建会话失败');
        set({ error: errorMessage, loading: false, processing: false });
      }
    } finally {
      set({
        sseConnected: false,
        sseAbortController: null,
        loading: false,
        processing: false,
        streaming: {
          isStreaming: false,
          streamingMessageId: null,
          streamingContent: '',
          streamingRole: '',
          streamingSenderName: '',
          parts: [],
          sequence: 0,
        },
      });
    }
  },

  loadSession: async (sessionId: string) => {
    const state = get();
    if (state.loading) return;

    set({ loading: true, error: null });

    try {
      const api = getAgentTeamApi();
      const response = await api.getSession(sessionId);

      const activeAgentsMap = new Map<string, ActiveAgent>();
      const taskList = (response.session.metadata as any)?.task_list;
      if (taskList?.tasks) {
        taskList.tasks.forEach((task: any) => {
          if (task.assignee_name) {
            activeAgentsMap.set(task.assignee_name, {
              agent_id: null,
              agent_name: task.assignee_name,
              agent_role: 'executor',
              status: 'idle',
              task_id: typeof task.id === 'string' ? null : task.id,
              task_description: task.description,
              task_type: task.task_type,
              tools: [],
              progress: 0,
              last_update: new Date().toISOString(),
            });
          }
        });
      }

      set({
        session: response.session,
        workspaceId: response.session.workspace_id,
        conversations: response.conversations || [],
        activeAgents: activeAgentsMap,
        loading: false,
      });

      get().saveActiveSession();
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '加载会话失败');
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  addMessage: (message: AgentConversationMessage) => {
    set(state => ({
      conversations: [...state.conversations, message],
    }));
  },

  updateStreamingMessage: (content: string) => {
    set(state => ({
      streaming: {
        ...state.streaming,
        streamingContent: content,
      },
    }));
  },

  finishStreamingMessage: (messageId: number, fullContent: string) => {
    const state = get();

    const finalMessage: AgentConversationMessage = {
      id: messageId,
      session_id: state.session?.id || '',
      sequence_number: state.conversations.length + 1,
      role: state.streaming.streamingRole as any,
      speaker_name: state.streaming.streamingSenderName,
      agent_id: null,
      message_type: 'text',
      content: fullContent,
      data: state.streaming.parts.length > 0 ? { parts: state.streaming.parts } : null,
      is_final: true,
      created_at: new Date().toISOString(),
    };

    set({
      conversations: [...state.conversations, finalMessage],
      streaming: {
        isStreaming: false,
        streamingMessageId: null,
        streamingContent: '',
        streamingRole: '',
        streamingSenderName: '',
        parts: [],
        sequence: 0,
      },
      processing: false,
    });
  },

  updateAgentStatus: (update: AgentStatusUpdate) => {
    set(state => {
      const agents = new Map(state.activeAgents);
      const existing = agents.get(update.agent_name);

      if (existing) {
        agents.set(update.agent_name, {
          ...existing,
          status: update.status,
          last_update: new Date().toISOString(),
        });
      }

      return { activeAgents: agents };
    });
  },

  addActiveAgent: (agent: ActiveAgent) => {
    set(state => {
      const agents = new Map(state.activeAgents);
      agents.set(agent.agent_name, agent);
      return { activeAgents: agents };
    });
  },

  removeActiveAgent: (agentName: string, _reason?: string) => {
    set(state => {
      const agents = new Map(state.activeAgents);
      const existing = agents.get(agentName);

      if (existing) {
        agents.set(agentName, {
          ...existing,
          status: 'retired',
          last_update: new Date().toISOString(),
        });
      }

      return { activeAgents: agents };
    });
  },

  updateAgentProgress: (agentName: string, progress: number, _message?: string) => {
    set(state => {
      const agents = new Map(state.activeAgents);
      const existing = agents.get(agentName);

      if (existing) {
        agents.set(agentName, {
          ...existing,
          progress,
          status: progress >= 100 ? 'completed' : 'executing',
          last_update: new Date().toISOString(),
        });
      }

      return { activeAgents: agents };
    });
  },

  updateSessionStatus: (status: SessionStatus) => {
    set(state => {
      if (state.session) {
        return {
          session: {
            ...state.session,
            status,
            updated_at: new Date().toISOString(),
          },
        };
      }
      return {};
    });
  },

  clearSession: () => {
    set({
      session: null,
      workspaceId: null,
      conversations: [],
      activeAgents: new Map(),
      loading: false,
      error: null,
      processing: false,
      streaming: {
        isStreaming: false,
        streamingMessageId: null,
        streamingContent: '',
        streamingRole: '',
        streamingSenderName: '',
        parts: [],
        sequence: 0,
      },
      stages: [],
      pendingQuestion: null,
    });
  },

  setProcessing: (processing: boolean) => {
    set({ processing });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  fetchSessions: async (workspaceId: number, page: number = 1, pageSize: number = 20) => {
    try {
      const api = getAgentTeamApi();
      const response = await api.listSessions({
        workspaceId,
        page,
        pageSize,
      });
      set({ sessions: response.sessions, sessionsLoaded: true });
    } catch (error) {
      console.error('[AgentTeamStore] 获取会话列表失败:', error);
    }
  },

  deleteSession: async (_workspaceId: number, sessionId: string) => {
    try {
      const api = getAgentTeamApi();
      await api.endSession(sessionId);
      set(state => ({
        sessions: state.sessions.filter(s => s.id !== sessionId),
      }));
    } catch (error) {
      console.error('[AgentTeamStore] 删除会话失败:', error);
      throw error;
    }
  },

  startSSEStream: async (content: string) => {
    const session = get().session;
    if (!session) {
      get().setError('会话不存在');
      return;
    }

    const userMessage: AgentConversationMessage = {
      id: Date.now(),
      session_id: session.id,
      sequence_number: get().conversations.length + 1,
      role: 'user',
      speaker_name: '用户',
      agent_id: null,
      message_type: 'text',
      content,
      data: null,
      is_final: true,
      created_at: new Date().toISOString(),
    };

    const abortController = new AbortController();
    set(state => ({
      conversations: [...state.conversations, userMessage],
      sseAbortController: abortController,
      sseConnected: true,
      processing: true,
      streaming: {
        isStreaming: true,
        streamingMessageId: null,
        streamingContent: '',
        streamingRole: 'orchestrator',
        streamingSenderName: '规划者',
        parts: [],
        sequence: 0,
      },
    }));

    try {
      const api = getAgentTeamApi();
      const request: SendMessageRequest = { content };

      for await (const event of api.sendMessageStream(session.id, request, abortController.signal)) {
        get().handleSSEEvent(event);
        await new Promise<void>(r => setTimeout(r, 0));
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('[AgentTeamStore] SSE流已取消');
      } else {
        const errorMessage = sanitizeErrorMessage(error, '发送消息失败');
        get().setError(errorMessage);
      }
    } finally {
      set({
        sseConnected: false,
        sseAbortController: null,
        processing: false,
        streaming: {
          isStreaming: false,
          streamingMessageId: null,
          streamingContent: '',
          streamingRole: '',
          streamingSenderName: '',
          parts: [],
          sequence: 0,
        },
      });
    }
  },

  cancelSSEStream: () => {
    const controller = get().sseAbortController;
    if (controller) {
      controller.abort();
      set({
        sseConnected: false,
        sseAbortController: null,
        streaming: {
          isStreaming: false,
          streamingMessageId: null,
          streamingContent: '',
          streamingRole: '',
          streamingSenderName: '',
          parts: [],
          sequence: 0,
        },
        stages: [],
        processing: false,
      });
    }
  },

  handleSSEEvent: (event: SSEEvent) => {
    const data = event.data as any;

    switch (event.event) {
      case 'workflow.started': {
        const initialStages: WorkflowStageState[] = (data.stages || []).map(
          (stage: WorkflowStageName) => ({
            stage,
            label: STAGE_LABELS[stage] || stage,
            status: 'pending' as const,
            startedAt: null,
            completedAt: null,
            error: null,
          })
        );
        set({ stages: initialStages });
        break;
      }

      case 'stage.started': {
        const stage = data.stage as WorkflowStageName;
        set(state => ({
          stages: state.stages.map(s =>
            s.stage === stage
              ? { ...s, status: 'running' as const, startedAt: data.timestamp, error: null }
              : s
          ),
        }));
        break;
      }

      case 'stage.completed': {
        const stage = data.stage as WorkflowStageName;
        set(state => ({
          stages: state.stages.map(s =>
            s.stage === stage
              ? { ...s, status: 'completed' as const, completedAt: data.timestamp }
              : s
          ),
        }));
        break;
      }

      case 'stage.failed': {
        const stage = data.stage as WorkflowStageName;
        set(state => ({
          stages: state.stages.map(s =>
            s.stage === stage
              ? { ...s, status: 'failed' as const, error: data.error, completedAt: data.timestamp }
              : s
          ),
        }));
        break;
      }

      case 'workflow.completed': {
        break;
      }

      case 'task.started': {
        const taskId = data.task_id as number;
        if (taskId != null) {
          set(state => {
            const next = new Map(state.workerStreams);
            next.set(taskId, {
              taskId,
              agentType: data.agent_type || 'worker',
              description: data.description || '',
              status: 'running',
              streamingContent: '',
              parts: [],
              sequence: 0,
            });
            return { workerStreams: next };
          });
        }
        break;
      }

      case 'task.completed': {
        const taskId = data.task_id as number;
        if (taskId != null) {
          set(state => {
            const next = new Map(state.workerStreams);
            const ws = next.get(taskId);
            if (ws) {
              next.set(taskId, { ...ws, status: 'completed' });
            }
            return { workerStreams: next };
          });
          setTimeout(() => get().clearWorkerStream(taskId), 5000);
        }
        break;
      }

      case 'task.failed': {
        const taskId = data.task_id as number;
        if (taskId != null) {
          set(state => {
            const next = new Map(state.workerStreams);
            const ws = next.get(taskId);
            if (ws) {
              next.set(taskId, { ...ws, status: 'failed' });
            }
            return { workerStreams: next };
          });
          setTimeout(() => get().clearWorkerStream(taskId), 5000);
        }
        break;
      }

      case 'chat.stream': {
        const content = data.content || '';
        const taskId = data.task_id;

        if (taskId != null) {
          set(state => {
            const next = new Map(state.workerStreams);
            const ws = next.get(taskId);
            if (ws) {
              const parts = [...ws.parts];
              const lastTextIdx = parts.length - 1;
              if (lastTextIdx >= 0 && parts[lastTextIdx].type === 'text') {
                parts[lastTextIdx] = { ...parts[lastTextIdx], content: parts[lastTextIdx].content + content };
              } else {
                parts.push({ type: 'text', content, sequence: ws.sequence++ });
              }
              next.set(taskId, { ...ws, streamingContent: ws.streamingContent + content, parts, sequence: ws.sequence });
            }
            return { workerStreams: next };
          });
        } else {
          set(state => {
            const parts = [...state.streaming.parts];
            const lastTextIdx = parts.length - 1;
            if (lastTextIdx >= 0 && parts[lastTextIdx].type === 'text') {
              parts[lastTextIdx] = {
                ...parts[lastTextIdx],
                content: parts[lastTextIdx].content + content,
              };
            } else {
              parts.push({
                type: 'text',
                content,
                sequence: state.streaming.sequence++,
              });
            }
            return {
              streaming: {
                ...state.streaming,
                isStreaming: true,
                streamingContent: state.streaming.streamingContent + content,
                parts,
              },
            };
          });
        }
        break;
      }

      case 'chat.thinking': {
        const taskId = data.task_id;
        const thinkingProcess = (data.thinking_process as string) || '';

        if (taskId != null) {
          set(state => {
            const next = new Map(state.workerStreams);
            const ws = next.get(taskId);
            if (ws) {
              const parts = [...ws.parts];
              const reasoningIdx = parts.findIndex(p => p.type === 'reasoning');
              if (reasoningIdx >= 0) {
                parts[reasoningIdx] = { ...parts[reasoningIdx], content: thinkingProcess };
              } else {
                parts.unshift({ type: 'reasoning', content: thinkingProcess, sequence: ws.sequence++ });
              }
              next.set(taskId, { ...ws, parts, sequence: ws.sequence });
            }
            return { workerStreams: next };
          });
        } else {
          set(state => {
            const parts = [...state.streaming.parts];
            const reasoningIdx = parts.findIndex(p => p.type === 'reasoning');
            if (reasoningIdx >= 0) {
              parts[reasoningIdx] = { ...parts[reasoningIdx], content: thinkingProcess };
            } else {
              parts.unshift({
                type: 'reasoning',
                content: thinkingProcess,
                sequence: state.streaming.sequence++,
              });
            }
            return {
              streaming: { ...state.streaming, parts },
            };
          });
        }
        break;
      }

      case 'tool.detected': {
        const taskId = data.task_id;

        if (taskId != null) {
          set(state => {
            const next = new Map(state.workerStreams);
            const ws = next.get(taskId);
            if (ws) {
              const parts = [...ws.parts];
              const seq = ws.sequence;
              const toolPart: StreamingMessagePart = {
                type: 'tool',
                content: '',
                tool_call_id: data.tool_call_id,
                tool_name: data.tool_name,
                status: 'detected',
                tool_args: data.tool_args,
                sequence: seq,
              };
              parts.push(toolPart);
              next.set(taskId, { ...ws, parts, sequence: seq + 1 });
            }
            return { workerStreams: next };
          });
        } else {
          set(state => {
            const parts = [...state.streaming.parts];
            const seq = state.streaming.sequence;
            const existingIdx = parts.findIndex(
              p => p.type === 'tool' && p.tool_call_id === data.tool_call_id
            );
            const toolPart: StreamingMessagePart = {
              type: 'tool',
              content: '',
              tool_call_id: data.tool_call_id,
              tool_name: data.tool_name,
              status: 'detected',
              tool_args: data.tool_args,
              sequence: 0,
            };
            if (existingIdx >= 0) {
              toolPart.sequence = parts[existingIdx].sequence;
              parts[existingIdx] = toolPart;
            } else {
              const lastTextIdx = [...parts].reverse().findIndex(p => p.type === 'text');
              if (lastTextIdx >= 0) {
                const actualIdx = parts.length - 1 - lastTextIdx;
                toolPart.sequence = seq + 1;
                parts.splice(actualIdx + 1, 0, toolPart);
              } else {
                toolPart.sequence = seq;
                parts.push(toolPart);
              }
            }
            return {
              streaming: { ...state.streaming, parts, sequence: seq + 1 },
            };
          });
        }
        break;
      }

      case 'tool.executing': {
        const taskId = data.task_id;

        if (taskId != null) {
          set(state => {
            const next = new Map(state.workerStreams);
            const ws = next.get(taskId);
            if (ws) {
              const parts = [...ws.parts];
              const idx = parts.findIndex(p => p.type === 'tool' && p.tool_call_id === data.tool_call_id);
              if (idx >= 0) {
                parts[idx] = { ...parts[idx], status: 'executing' };
              }
              next.set(taskId, { ...ws, parts });
            }
            return { workerStreams: next };
          });
        } else {
          set(state => {
            const parts = [...state.streaming.parts];
            const idx = parts.findIndex(
              p => p.type === 'tool' && p.tool_call_id === data.tool_call_id
            );
            if (idx >= 0) {
              parts[idx] = { ...parts[idx], status: 'executing' };
            }
            return {
              streaming: { ...state.streaming, parts },
            };
          });
        }
        break;
      }

      case 'tool.completed': {
        const taskId = data.task_id;

        if (taskId != null) {
          set(state => {
            const next = new Map(state.workerStreams);
            const ws = next.get(taskId);
            if (ws) {
              const parts = [...ws.parts];
              const idx = parts.findIndex(p => p.type === 'tool' && p.tool_call_id === data.tool_call_id);
              if (idx >= 0) {
                parts[idx] = {
                  ...parts[idx],
                  status: data.status || (data.error ? 'failed' : 'success'),
                  result: data.result,
                  error: data.error,
                };
              }
              next.set(taskId, { ...ws, parts });
            }
            return { workerStreams: next };
          });
        } else {
          set(state => {
            const parts = [...state.streaming.parts];
            const idx = parts.findIndex(
              p => p.type === 'tool' && p.tool_call_id === data.tool_call_id
            );
            if (idx >= 0) {
              parts[idx] = {
                ...parts[idx],
                status: data.status || (data.error ? 'failed' : 'success'),
                result: data.result,
                error: data.error,
              };
            }
            return {
              streaming: { ...state.streaming, parts },
            };
          });
        }
        break;
      }

      case 'chat.end': {
        const taskId = data.task_id;

        if (taskId != null) {
          // Worker chat.end — do not add to conversations, the worker stream already captured the content
        } else {
          const state = get();
          const finalContent = data.content || state.streaming.streamingContent;

          const finalMessage: AgentConversationMessage = {
            id: Date.now(),
            session_id: state.session?.id || '',
            sequence_number: state.conversations.length + 1,
            role: state.streaming.streamingRole as any || 'orchestrator',
            speaker_name: state.streaming.streamingSenderName || '规划者',
            agent_id: null,
            message_type: 'text',
            content: finalContent,
            data: state.streaming.parts.length > 0 ? { parts: state.streaming.parts } : null,
            is_final: true,
            created_at: new Date().toISOString(),
          };

          set({
            conversations: [...state.conversations, finalMessage],
            streaming: {
              isStreaming: false,
              streamingMessageId: null,
              streamingContent: '',
              streamingRole: '',
              streamingSenderName: '',
              parts: [],
              sequence: 0,
            },
          });
        }
        break;
      }

      case 'question.asked': {
        const evt = data as QuestionAskedEvent;
        console.log('[AgentTeamStore] 处理提问事件 - question_id:', evt.question_id);
        set({ pendingQuestion: evt });
        break;
      }

      case 'error':
        set({
          error: data.error || data.message || '未知错误',
          streaming: {
            isStreaming: false,
            streamingMessageId: null,
            streamingContent: '',
            streamingRole: '',
            streamingSenderName: '',
            parts: [],
            sequence: 0,
          },
          processing: false,
        });
        break;
    }
  },

  handleQuestionAsked: (event: QuestionAskedEvent) => {
    console.log('[AgentTeamStore] 处理提问事件 - question_id:', event.question_id);
    set({ pendingQuestion: event });
  },

  submitQuestionAnswer: async (questionId: string, answers: string[][]) => {
    console.log('[AgentTeamStore] 提交问答答案 - questionId:', questionId);
    try {
      const api = getAgentTeamApi();
      await api.replyQuestion(questionId, answers);
      set({ pendingQuestion: null });
      console.log('[AgentTeamStore] 答案提交成功');
    } catch (error) {
      console.error('[AgentTeamStore] 提交答案失败:', error);
      throw error;
    }
  },

  dismissQuestion: async (questionId: string) => {
    console.log('[AgentTeamStore] 取消问题 - questionId:', questionId);
    try {
      const api = getAgentTeamApi();
      await api.dismissQuestion(questionId);
      set({ pendingQuestion: null });
      console.log('[AgentTeamStore] 问题已取消');
    } catch (error) {
      console.error('[AgentTeamStore] 取消问题失败:', error);
      throw error;
    }
  },

  setSseConnected: (connected: boolean) => {
    set({ sseConnected: connected });
  },

  cleanup: () => {
    const controller = get().sseAbortController;
    if (controller) {
      controller.abort();
    }

    set({
      sseConnected: false,
      sseAbortController: null,
      processing: false,
      streaming: {
        isStreaming: false,
        streamingMessageId: null,
        streamingContent: '',
        streamingRole: '',
        streamingSenderName: '',
        parts: [],
        sequence: 0,
      },
      workerStreams: new Map(),
      pendingQuestion: null,
      error: null,
    });
  },

  saveActiveSession: () => {
    const { session, workspaceId } = get();
    if (session && workspaceId) {
      localStorage.setItem(`agentTeam_activeSession_${workspaceId}`, session.id);
    }
  },

  getSavedActiveSessionId: (workspaceId: number) => {
    return localStorage.getItem(`agentTeam_activeSession_${workspaceId}`);
  },

  clearSavedSession: () => {
    const { workspaceId } = get();
    if (workspaceId) {
      localStorage.removeItem(`agentTeam_activeSession_${workspaceId}`);
    }
  },

  initializeWorkspace: async (workspaceId: number) => {
    set({ workspaceId, loading: true, error: null });

    try {
      const savedSessionId = localStorage.getItem(`agentTeam_activeSession_${workspaceId}`);

      if (savedSessionId) {
        try {
          await get().loadSession(savedSessionId);

          const status = await get().checkExecutionStatus(savedSessionId);
          if (status.is_executing) {
            await get().subscribeProgress(savedSessionId);
          }

          return;
        } catch (e) {
          localStorage.removeItem(`agentTeam_activeSession_${workspaceId}`);
        }
      }

      set({ loading: false });

    } catch (e) {
      set({ error: sanitizeErrorMessage(e), loading: false });
    }
  },

  checkExecutionStatus: async (sessionId: string) => {
    const response = await fetch(`/api/agent-team/v2/sessions/${sessionId}/execution-status`);
    if (!response.ok) {
      throw new Error('获取执行状态失败');
    }
    return response.json();
  },

  subscribeProgress: async (sessionId: string) => {
    const existingController = get().sseAbortController;
    if (existingController) {
      existingController.abort();
    }

    const controller = new AbortController();
    set({ sseAbortController: controller, sseConnected: true, processing: true });

    try {
      const token = useAuthStore.getState().token;
      const response = await fetch(
        `/api/agent-team/v2/sessions/${sessionId}/progress-stream`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error('订阅进度失败');
      }

      const reader = response.body?.getReader();
      if (!reader) return;

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = '';

        for (const line of lines) {
          if (line.startsWith('data:')) {
            const dataStr = line.slice(5).trim();
            if (dataStr) {
              try {
                const data = JSON.parse(dataStr);
                get().handleSSEEvent({ type: 'message', data });
              } catch {
              }
            }
          }
        }
      }

    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[AgentTeamStore] SSE订阅已断开');
      } else {
        set({ error: sanitizeErrorMessage(error) });
      }
    } finally {
      set({ sseConnected: false, sseAbortController: null, processing: false });
    }
  },

  cancelExecution: async (sessionId: string) => {
    const response = await fetch(`/api/agent-team/v2/sessions/${sessionId}/cancel`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error('取消执行失败');
    }
    const data = await response.json();
    return data.cancelled;
  },

  sendMessageToQueue: async (sessionId: string, content: string) => {
    const token = useAuthStore.getState().token;
    const response = await fetch(`/api/agent-team/v2/sessions/${sessionId}/enqueue-message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content }),
    });
    if (!response.ok) {
      throw new Error('消息入队失败');
    }
    const data = await response.json();
    return data;
  },
}));

export default useAgentTeamStore;